import React, { useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { useAuthStore } from './store/useAuthStore';
import { useInactivityLogout } from './hooks/useInactivityLogout';
import InactivityWarning from './components/InactivityWarning/InactivityWarning';
import { ThemeProvider } from './contexts/ThemeContext';

import HomePage               from './pages/HomePage';
import AuthForm               from './pages/AuthForm';
import Dashboard              from './pages/Dashboard';
import PrivateRoute           from './components/PrivateRoute';
import InteractiveMap         from './components/InteractiveMap/InteractiveMap';
import GeodesyCoordinateConverter from './components/GeodesyCoordinateConverter/GeodesyCoordinateConverter';
import StorageManager         from './components/StorageManager/StorageManager';
import DXFViewer2D            from './components/DXFViewer2D/DXFViewer2D';
import MesPoints              from './pages/MesPoints';
import Panier                from './pages/Panier';
import { CartProvider }      from './contexts/CartContext';

// ── Admin Géo ────────────────────────────────────────────────────────────────
import AdminGeoLayout     from './layouts/AdminGeoLayout';
import AdminGeoDashboard  from './pages/admin-geo/AdminGeoDashboard';
import AdminGeoPlaceholder from './pages/admin-geo/AdminGeoPlaceholder';
import GeodesyManager    from './pages/admin-geo/GeodesyManager';
import LimitesAdmin      from './pages/admin-geo/LimitesAdmin';

// ── Gestion déconnexion forcée (401 + refresh échoué) ────────────────────────
function AuthLogoutListener() {
  useEffect(() => {
    const handler = () => useAuthStore.logout();
    window.addEventListener('madjeo:logout', handler);
    return () => window.removeEventListener('madjeo:logout', handler);
  }, []);
  return null;
}

// ── Déconnexion automatique par inactivité ────────────────────────────────────
// Séparé dans un sous-composant car useNavigate() exige d'être dans le Router
function InactivityGuard() {
  const navigate = useNavigate();

  // useCallback stabilise la référence : évite que l'arrow function recréée
  // à chaque render ne redémarre les timers via les deps du hook.
  const handleLogout = useCallback(
    () => navigate('/login', { replace: true }),
    [navigate],
  );

  const { showWarning, secondsLeft, stayConnected, logout } = useInactivityLogout({
    timeoutMs: 20 * 60 * 1000,   // 20 minutes d'inactivité → déconnexion
    warningMs:  2 * 60 * 1000,   // avertissement 2 minutes avant
    onLogout: handleLogout,
  });

  if (!showWarning) return null;

  return (
    <InactivityWarning
      secondsLeft={secondsLeft}
      onStay={stayConnected}
      onLogout={logout}
    />
  );
}

// ── App principale ────────────────────────────────────────────────────────────
export default function App() {
  return (
    <ThemeProvider>
    <CartProvider>
    <Router>
      <AuthLogoutListener />
      <InactivityGuard />

      <Routes>
        {/* Publiques */}
        <Route path="/"      element={<HomePage />} />
        <Route path="/login" element={<AuthForm />} />
        <Route path="/cad"   element={<PrivateRoute><DXFViewer2D /></PrivateRoute>} />

        {/* Privées — Dashboard comme layout parent */}
        <Route
          path="/expert"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        >
          <Route index                   element={<InteractiveMap />} />
          <Route path="maps"             element={<InteractiveMap />} />
          <Route path="userFile"         element={<StorageManager />} />
          <Route path="transLaborde"     element={<GeodesyCoordinateConverter />} />
          <Route path="mes-points"       element={<MesPoints />} />
          <Route path="panier"           element={<Panier />} />
        </Route>

        {/* Admin Géo — interface SIG séparée */}
        <Route
          path="/admin-geo"
          element={
            <PrivateRoute requiredRole="admin_geo">
              <AdminGeoLayout />
            </PrivateRoute>
          }
        >
          <Route index                  element={<AdminGeoDashboard />} />
          <Route path="geodesy"         element={<GeodesyManager />} />
          <Route path="altimetrie"      element={<AdminGeoPlaceholder title="Repères altimétriques" />} />
          <Route path="limites"         element={<LimitesAdmin />} />
          <Route path="routes"          element={<AdminGeoPlaceholder title="Réseaux routiers" />} />
          <Route path="batiments"       element={<AdminGeoPlaceholder title="Bâtiments" />} />
          <Route path="couches"         element={<AdminGeoPlaceholder title="Couches SIG" />} />
          <Route path="referentiels"    element={<AdminGeoPlaceholder title="Référentiels géographiques" />} />
          <Route path="validation"      element={<AdminGeoPlaceholder title="Validation topologique" />} />
          <Route path="import"          element={<AdminGeoPlaceholder title="Import / Export" />} />
          <Route path="audit"           element={<AdminGeoPlaceholder title="Historique & Audit" />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={
          <div style={{ textAlign: 'center', padding: '5rem', fontSize: '1.25rem', color: '#64748b' }}>
            404 — Page introuvable
          </div>
        } />
      </Routes>
    </Router>
    </CartProvider>
    </ThemeProvider>
  );
}
